import React from "react";
import { Guide } from "../../../types/guide";
import GuideStatusBadge from "../../../components/GuideStatusBadge";
import {
    avatarStyle,
    badgeRowStyle,
    badgeStyle,
    cardStyle,
    infoRowStyle,
    mutedTextStyle,
    sectionTitleStyle,
} from "../GuideDetailModal.styles";
import { ChipList, formatCurrency, formatDate } from "../GuideDetailModal.utils";
import { useZipLocation } from "../../../hooks/useZipLocation";

export function OverviewTab({ guide }: { guide: Guide }) {
    const onboarding = guide.onboarding;
    const rawLocation = onboarding?.basicInfo?.location ?? null;
    const bio = onboarding?.basicInfo?.bio;
    const enhancedProfile = onboarding?.enhancedProfile;
    const locationLabel = useZipLocation(rawLocation, "Location not provided");
    const hasLocation = Boolean(rawLocation && rawLocation.trim().length > 0);
    
    // Use enhanced profile data when available, fallback to basic info
    const displayBio = enhancedProfile?.about || bio;
    const profileImageUrl = enhancedProfile?.imageUrl;

    return (
        <>
            <div style={{ marginBottom: 20, display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ position: "relative" }}>
                    {profileImageUrl ? (
                        <img
                            src={profileImageUrl}
                            alt={`${guide.name} profile`}
                            style={{
                                width: 48,
                                height: 48,
                                borderRadius: "50%",
                                objectFit: "cover",
                                border: "1px solid #2C2731",
                                boxShadow: "0 0 0 3px #FEF6D6",
                            }}
                        />
                    ) : (
                        <div style={avatarStyle}>{guide.name ? guide.name.charAt(0).toUpperCase() : "G"}</div>
                    )}
                    {guide.backgroundChecked === true && (
                        <div 
                            style={{
                                position: "absolute",
                                bottom: -2,
                                right: -2,
                                width: 16,
                                height: 16,
                                backgroundColor: "#10b981",
                                borderRadius: "50%",
                                border: "2px solid white",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: 10,
                                color: "white",
                                fontWeight: "bold"
                            }}
                            title="Background Verified"
                        >
                            ✓
                        </div>
                    )}
                </div>
                <div>
                    <div className="ph-mask" style={{ fontWeight: 600, fontSize: 18 }}>{guide.name}</div>
                    <div className="ph-mask" style={mutedTextStyle}>{guide.email}</div>
                </div>
            </div>

            <div style={badgeRowStyle}>
                <GuideStatusBadge status={guide.status} />
                <span style={badgeStyle}>Joined {formatDate(guide.joinedAt)}</span>
                {hasLocation && <span style={badgeStyle}>Based in {locationLabel}</span>}
            </div>

            <div
                style={{
                    display: "flex",
                    gap: 12,
                    alignItems: "center",
                    color: "#2C2731",
                    fontSize: 13,
                    marginBottom: 16,
                    flexWrap: "wrap",
                }}
            >
                <span className="ph-mask">Email: {guide.email}</span>
                <span className="ph-mask">Phone: {guide.phone || "Not provided"}</span>
                {hasLocation && <span>Location: {locationLabel}</span>}
            </div>

            <div style={cardStyle}>
                <div style={sectionTitleStyle}>About</div>
                <div className="ph-mask" style={{ color: "#2C2731", fontSize: 14, lineHeight: 1.5 }}>
                    {displayBio ? displayBio : "Background information has not been provided yet."}
                </div>
                {enhancedProfile && (
                    <div style={{ marginTop: 8, ...mutedTextStyle }}>
                        Last updated {formatDate(enhancedProfile.updatedAt ?? enhancedProfile.createdAt)}
                    </div>
                )}
            </div>

            {guide.specialties?.length ? (
                <div style={cardStyle}>
                    <div style={sectionTitleStyle}>Specializations</div>
                    <ChipList values={guide.specialties} empty="No specializations listed" />
                </div>
            ) : null}

            <div style={cardStyle}>
                <div style={sectionTitleStyle}>At a Glance</div>
                <div style={{ display: "grid", gap: 6, fontSize: 13, color: "#2C2731" }}>
                    <div style={infoRowStyle}>
                        <span>Connected Seekers</span>
                        <span>{guide.connectedSeekers}</span>
                    </div>
                    <div style={infoRowStyle}>
                        <span>Income Generated</span>
                        <span>{formatCurrency(guide.income)}</span>
                    </div>
                    <div style={infoRowStyle}>
                        <span>Latest Background Check</span>
                        <span>
                            {onboarding?.backgroundChecks && onboarding.backgroundChecks.length
                                ? formatDate(
                                      onboarding.backgroundChecks[0].submittedAt ?? onboarding.backgroundChecks[0].createdAt,
                                  )
                                : "Not submitted"}
                        </span>
                    </div>
                </div>
            </div>
        </>
    );
}
